// Shared, pure calculation helpers so inline property tools match the
// dedicated calculator pages exactly.

export const STAMP_DUTY = {
  Maharashtra: { male: 6, female: 5, registration: 1 },
  Karnataka: { male: 5, female: 5, registration: 1 },
  Delhi: { male: 6, female: 4, registration: 1 },
  Tamil_Nadu: { male: 7, female: 7, registration: 4 },
  Telangana: { male: 4, female: 4, registration: 0.5 },
  Uttar_Pradesh: { male: 7, female: 6, registration: 1 },
  Gujarat: { male: 4.9, female: 4.9, registration: 1 },
  Haryana: { male: 7, female: 5, registration: 1 },
  West_Bengal: { male: 6, female: 6, registration: 1 },
  Rajasthan: { male: 6, female: 5, registration: 1 },
  Kerala: { male: 8, female: 8, registration: 2 },
  Punjab: { male: 7, female: 5, registration: 1 },
  Madhya_Pradesh: { male: 7.5, female: 7.5, registration: 3 },
  Goa: { male: 5, female: 4, registration: 3 },
};

// Map a free-text state name to a STAMP_DUTY key (best effort).
export const stateKeyFrom = (name) => {
  if (!name) return 'Maharashtra';
  const norm = name.trim().replace(/\s+/g, '_');
  if (STAMP_DUTY[norm]) return norm;
  const found = Object.keys(STAMP_DUTY).find((k) => k.replace(/_/g, ' ').toLowerCase() === name.trim().toLowerCase());
  return found || 'Maharashtra';
};

export const calcEMI = (principal, annualRate, months) => {
  const P = Number(principal) || 0;
  const n = Number(months) || 0;
  const r = (Number(annualRate) || 0) / 12 / 100;
  if (!P || !n) return { emi: 0, totalInterest: 0, totalAmount: 0, principal: P };
  let emi;
  if (r === 0) emi = P / n;
  else emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  const totalAmount = emi * n;
  return { emi: Math.round(emi), totalInterest: Math.round(totalAmount - P), totalAmount: Math.round(totalAmount), principal: P };
};

export const calcStampDuty = (value, stateKey, gender, propertyType) => {
  const rates = STAMP_DUTY[stateKey] || STAMP_DUTY.Maharashtra;
  let stampPct = rates[gender] ?? rates.male;
  const regPct = rates.registration;
  if (propertyType === 'commercial') stampPct += 1;
  const stamp = (value * stampPct) / 100;
  const reg = (value * regPct) / 100;
  return { stamp, reg, total: stamp + reg, stampPct, regPct };
};

export const calcRentalYield = (value, monthlyRent, maintenance, propertyTax, appreciation, years) => {
  const annualRent = monthlyRent * 12;
  const annualMaintenance = maintenance * 12;
  const netAnnualIncome = annualRent - annualMaintenance - propertyTax;
  const grossYield = value ? (annualRent / value) * 100 : 0;
  const netYield = value ? (netAnnualIncome / value) * 100 : 0;
  const futureValue = value * Math.pow(1 + appreciation / 100, years);
  const totalRentEarned = netAnnualIncome * years;
  const totalReturn = futureValue - value + totalRentEarned;
  const roiPercent = value ? (totalReturn / value) * 100 : 0;
  return { annualRent, netAnnualIncome, grossYield, netYield, futureValue, totalReturn, roiPercent };
};

export const calcAffordability = (income, expenses, downPayment, tenureYears, rate, emiRatio) => {
  const maxEmi = Math.max(0, (income * emiRatio) / 100 - 0); // simple: % of income
  const n = tenureYears * 12;
  const r = rate / 12 / 100;
  let loan = 0;
  if (maxEmi > 0 && n > 0) {
    loan = r === 0 ? maxEmi * n : (maxEmi * (Math.pow(1 + r, n) - 1)) / (r * Math.pow(1 + r, n));
  }
  const maxProperty = loan + downPayment;
  const surplus = income - expenses - maxEmi;
  return { maxEmi: Math.round(maxEmi), loan: Math.round(loan), maxProperty: Math.round(maxProperty), surplus: Math.round(surplus) };
};

export const inr = (n) => {
  const v = Number(n) || 0;
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2).replace(/\.?0+$/, '')} Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2).replace(/\.?0+$/, '')} L`;
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
};
